import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@heroicons/react/24/outline';
import { ui } from '@procuros/datachecks';

import { useNavigationContext } from 'contexts/SchemableFormNavigationContext/SchemableFormNavigationContext';
import { DisplayModes } from 'services/webedi/types';
import { usePackingListStore } from 'stores/packingList/packingList';
import { isDefined } from 'support/helpers/generic/generic';
import { DocumentType } from 'support/types';
import { DataTable, DataTableHandle } from 'components/DataTable';
import { DataTableProvider, useDataTableContext } from 'components/DataTable/contexts/DataTableContext';
import { Button } from 'components/Form/Button';
import { MenuButton, MenuButtonOption } from 'components/MenuButton';

import { useAppendLineItem } from './hooks/useAppendLineItem';
import { DocumentSectionAnnotation } from '../DocumentSectionAnnotation/DocumentSectionAnnotation';
import { useGenerateLineItemsColumns } from '../useGenerateLineItemsColumns';
import { WebEDISection } from '../WebEDISection/WebEDISection';
import { BATCHES_SUFFIX, scrollToBatch } from './helpers';

export const DocumentLineItems = memo(
  ({ config, mode, documentType, isSubmitting, baseData, revisedData }: DocumentLineItemsProps) => {
    const packingListEnabled = usePackingListStore((state) => state.enabled);
    const { options, fields } = config;
    if (!fields?.length) {
      return null;
    }

    return (
      <DataTableProvider name={DATA_PREFIX} baseData={baseData} revisedData={revisedData}>
        <LineItemsTable
          mode={mode}
          config={config}
          numberOfPinnedColumns={options?.pinned_column_count ?? 2}
          documentType={documentType}
          isSubmitting={isSubmitting}
          packingListEnabled={packingListEnabled}
        />
      </DataTableProvider>
    );
  },
);

DocumentLineItems.displayName = 'DocumentLineItems';

type HeaderMenuProps = {
  dataPrefix: string;
  mode: DisplayModes;
  documentType: DocumentType;
  configuration: ui.LineItemsSection['fields'];
  isSubmitting: boolean;
  packingListEnabled?: boolean;
};

const HeaderMenu = ({
  dataPrefix,
  mode,
  configuration,
  documentType,
  isSubmitting,
  packingListEnabled,
}: HeaderMenuProps) => {
  const hasBatchColumns = configuration.some((field) => field.path.includes(BATCHES_SUFFIX));
  const { t } = useTranslation();
  const { fields, setValue } = useDataTableContext();
  const appendLineItem = useAppendLineItem(documentType, packingListEnabled);

  if (mode === DisplayModes.view || isSubmitting) {
    return undefined;
  }

  const actions = [
    {
      label: t('webedi:line_items.header_button.new_line_item.text'),
      onClick: appendLineItem,
      analyticsId: 'webedi:line_item_header_add',
    },

    hasBatchColumns
      ? {
          label: t('webedi:line_items.header_button.new_batch.text'),
          onClick: () => {
            const batches = fields[0][BATCHES_SUFFIX];
            const newBatches = batches && Array.isArray(batches) ? batches.concat({}) : [{}];
            const numberOfBatches = newBatches.length;
            setValue(BATCHES_PATH, newBatches, { shouldDirty: true });

            setTimeout(() => {
              scrollToBatch(dataPrefix, numberOfBatches);
            }, 1000);
          },
          analyticsId: 'webedi:line_item_header_add_batch',
        }
      : null,
  ].filter(isDefined);

  return (
    <MenuButton
      text={t('webedi:line_items.header_button.text')}
      Icon={PlusIcon}
      variant="secondary"
      size="base"
      analyticsId="webedi:line_item_header_add"
    >
      {actions.map((action) => (
        <MenuButtonOption
          key={action.label}
          onClick={action.onClick}
          className="border-b border-gray-100 last:border-b-0"
          analyticsId={action.analyticsId}
        >
          {action.label}
        </MenuButtonOption>
      ))}
    </MenuButton>
  );
};

type FooterMenuProps = {
  mode: DisplayModes;
  documentType: DocumentType;
  isSubmitting: boolean | undefined;
  packingListEnabled?: boolean;
};
const FooterMenu = ({ mode, documentType, isSubmitting, packingListEnabled }: FooterMenuProps) => {
  const { t } = useTranslation();
  const appendLineItem = useAppendLineItem(documentType, packingListEnabled);

  if (mode === DisplayModes.view || isSubmitting) {
    return undefined;
  }

  return (
    <div className="flex items-center space-x-3">
      <Button
        size="extra-small"
        variant="secondary"
        LeftIcon={PlusIcon}
        onClick={appendLineItem}
        analyticsId="webedi:line_items_add"
      >
        {t('webedi:line_items.add_button.text')}
      </Button>
    </div>
  );
};

const LineItemsTable = ({
  mode,
  config,
  numberOfPinnedColumns,
  documentType,
  isSubmitting,
  packingListEnabled,
}: {
  mode: DisplayModes;
  config: Omit<ui.LineItemsSection, 'type'>;
  numberOfPinnedColumns: number | undefined;
  documentType: DocumentType;
  isSubmitting: boolean;
  packingListEnabled?: boolean;
}) => {
  const { t } = useTranslation();
  const { service } = useNavigationContext();
  const { fields, baseData, revisedData } = useDataTableContext();
  const { columns, pinnedColumns } = useGenerateLineItemsColumns({
    config: config.fields,
    mode,
    documentType,
    numberOfPinnedColumns,
    packingListEnabled,
  });
  const dataTableRef = useRef<DataTableHandle>(null);

  useEffect(() => {
    // Expand first: a collapsed row renders no `data-field` for its embedded items, so
    // `scrollToNavigableItem` would fail to find the field it is navigating to.
    service.registerVirtualizedScroll('line_items', (index) => {
      dataTableRef.current?.expandRow(index);
      dataTableRef.current?.scrollToIndex(index);
    });
    return () => service.unregisterVirtualizedScroll('line_items');
  }, [service]);

  return (
    <WebEDISection
      header={{
        title: t('webedi:line_items.section_title', { count: fields.length, numberOfLineItems: fields.length }),
        spacing: 'medium',
        description: config.description,
      }}
    >
      <div className="space-y-4">
        <DocumentSectionAnnotation path={DATA_PREFIX} sectionConfig={config} />

        <DataTable
          ref={dataTableRef}
          pinnedColumns={pinnedColumns}
          key={mode}
          id="line_items"
          meta={{ dataPrefix: DATA_PREFIX, mode, isSubmitting }}
          columns={columns}
          data={fields}
          baseData={baseData as typeof fields}
          revisedData={revisedData as typeof fields}
          enableGlobalSearch={true}
          searchPositionStrategy="absolute"
          emptyState={
            mode === DisplayModes.create || mode === DisplayModes.edit
              ? t('webedi:line_items.empty_state.create.text')
              : t('webedi:line_items.empty_state.view.text')
          }
          headerMenu={
            <HeaderMenu
              dataPrefix={DATA_PREFIX}
              mode={mode}
              documentType={documentType}
              configuration={config.fields}
              isSubmitting={isSubmitting}
              packingListEnabled={packingListEnabled}
            />
          }
          footerMenu={
            <FooterMenu
              mode={mode}
              documentType={documentType}
              isSubmitting={isSubmitting}
              packingListEnabled={packingListEnabled}
            />
          }
        />
      </div>
    </WebEDISection>
  );
};

type DocumentLineItemsProps = {
  config: Omit<ui.LineItemsSection, 'type'>;
  mode: DisplayModes;
  documentType: DocumentType;
  isSubmitting: boolean;
  baseData?: Array<unknown>;
  revisedData?: Array<unknown>;
};

const DATA_PREFIX = 'line_items';
const BATCHES_PATH = `${DATA_PREFIX}.0.${BATCHES_SUFFIX}`;
